import React, { ReactNode, useEffect } from 'react'

import {
  Alert,
  BlankSlate,
  Box,
  EnhancedDimension,
  Icon,
  Link,
  Spinner,
  Text,
  Title,
} from '@factorialco/deprecated-design-system'
import { F0Button, StandardLayout } from '@factorialco/f0-react'

import ErrorBoundary from 'modules/core/components/ErrorBoundary'
import i18n from 'modules/core/lib/i18n'
import { RouteMeta } from 'modules/core/lib/navigation/types'

import { useRemoveFromFavorites } from 'modules/layout/components/ResponsivePage/FactorialOnePage/useFavorites'
import { useResponsivePage, useRouteMeta } from 'modules/layout/components/ResponsivePage/hooks'
import { useSidebarFavorites } from 'modules/layout/components/RootLayout/FactorialOneLayout/SidebarFavoritesProvider'

import { ResponsivePageActionsInterface } from '../_PageHeader'
import { TransactionalActions } from './_TransactionalActions'

export type Banner = RouteMeta['banner']

interface Header {
  /**
   * @deprecated This property should not be used as it's not supported in Factorial One.
   */
  illustration?: React.ComponentProps<typeof Icon.Large>['icon']

  /**
   * @deprecated This property should not be used as it's not supported in Factorial One.
   */
  illustrationComponent?: React.ReactNode

  /**
   * @deprecated This property should not be used as it's not supported in Factorial One.
   */
  title?: string

  /**
   * @deprecated This property should not be used as it's not supported in Factorial One.
   */
  titleSuffix?: string

  /**
   * @deprecated This property should not be used as it's not supported in Factorial One.
   */
  description?: string

  /**
   * @deprecated This property should not be used as it's not supported in Factorial One.
   */
  link?: React.ComponentProps<typeof Title>['link']

  /**
   * @deprecated This property should not be used as it's not supported in Factorial One.
   */
  widget?: React.ReactNode

  /**
   * @deprecated This property should not be used as it's not supported in Factorial One.
   */
  backLink?: React.ComponentProps<typeof Title>['link']

  /**
   * @deprecated This property should not be used as it's not supported in Factorial One.
   */
  attributes?: React.ComponentProps<typeof Title>['attributes']
}

type BaseProperties = {
  /**
   * 'narrow' , 'wide' and 'full' are deprecated. When set, the page will be displayed in the 'standard' variant.
   */
  variant?: 'standard' | 'fullscreen' | 'narrow' | 'wide' | 'full'

  /**
   * @deprecated This property should not be used as it's not supported in Factorial One.
   */
  showHeader?: boolean
  children: ReactNode

  /**
   * @deprecated This property should not be used as it's not supported in Factorial One.
   */
  actions?: ResponsivePageActionsInterface

  /**
   * @deprecated This property should not be used as it's not supported in Factorial One.
   */
  status?: RouteMeta['status']

  /**
   * @deprecated This property should not be used as it's not supported in Factorial One.
   */
  banner?: Banner

  /**
   * @deprecated This property should not be used as it's not supported in Factorial One.
   */
  widget?: ReactNode

  /**
   * @deprecated This property should not be used as it's not supported in Factorial One.
   */
  header?: Header
  /**
   * @deprecated This property should not be used as it's not supported in Factorial One.
   */
  transactionalActions?: TransactionalActions

  /**
   * @deprecated This property should not be used as it's not supported in Factorial One.
   */
  backLink?: React.ComponentProps<typeof Title>['link']
  authorized?: boolean
  height?: EnhancedDimension
  onActionClick?: (key: string) => void
}

type BodyProperties = BaseProperties &
  (
    | {
        loading: boolean
        error: unknown
      }
    | { loading?: never; error?: never }
  )

export function Body(props: BodyProperties) {
  const { setActionHandler, routeMeta } = useResponsivePage()
  const {
    variant = 'standard',
    showHeader = true,
    widget: bodyWidget,
    children,
    loading,
    error,
    status,
    banner,
    header,
    backLink,
    authorized = true,
  } = props

  const { data = {} } = useRouteMeta(routeMeta) ?? {}
  const { idByUrl: favoriteIdByUrl } = useSidebarFavorites()
  const [removeFromFavorites, { loading: isRemovingFavorite }] = useRemoveFromFavorites()
  const pathname = routeMeta?.pathname ?? ''
  const isResourcePage = routeMeta?.resource ?? false
  const hasResourceHeader =
    data === undefined ? false : 'headerComponent' in data || 'headerComponentProps' in data
  const headerRenderStrategy = getHeaderRenderStrategy({
    showHeader,
    hasResourceHeader,
    isResourcePage,
  })

  useEffect(() => {
    setActionHandler(() => props.onActionClick)
  }, [props.onActionClick, setActionHandler])

  const bannerWithFallback = banner ?? (data && 'banner' in data ? data.banner : undefined)
  const widget = header?.widget ?? bodyWidget

  return (
    <Container {...{ ...props, variant }}>
      <ErrorBoundary blankSlate>
        {loading ? (
          <Box alignItems="center" justifyContent="center" height="full">
            <Spinner />
          </Box>
        ) : error ? (
          <BlankSlate illustration="alert" title="" description="" />
        ) : authorized ? (
          <>
            {bannerWithFallback && (
              <Box>
                <Alert color={bannerWithFallback.color}>
                  {bannerWithFallback.icon && (
                    <Box flexNoShrink>
                      <Icon.Medium icon={bannerWithFallback.icon} />
                    </Box>
                  )}
                  {bannerWithFallback.link ? (
                    <>
                      <Text.Normal>{bannerWithFallback.message}</Text.Normal>
                      <Link.Router to={bannerWithFallback.link.href}>
                        {bannerWithFallback.link.label}
                      </Link.Router>
                    </>
                  ) : (
                    <Text.Normal>{bannerWithFallback.message} </Text.Normal>
                  )}
                </Alert>
              </Box>
            )}
            {((headerRenderStrategy === 'widget' && widget) || headerRenderStrategy === 'all') && (
              <Box gap="s16">
                {/* there are widgets on dashboard like pages we want to support even when using FactorialOne */}
                {headerRenderStrategy === 'widget' && widget && (
                  <Box paddingBottom="s8" gap="s8">
                    <Box justifyContent="center">{widget}</Box>
                  </Box>
                )}
                {headerRenderStrategy === 'all' && (
                  <Title
                    title={header?.title ?? data.title ?? ''}
                    titleSuffix={
                      header?.titleSuffix ?? ('titleSuffix' in data ? data.titleSuffix : undefined)
                    }
                    description={
                      header?.description ?? ('description' in data ? data.description : undefined)
                    }
                    tag={status ?? ('status' in data ? data.status : undefined)}
                    illustrationComponent={header?.illustrationComponent}
                    illustration={
                      header?.illustration ??
                      ('illustration' in data ? data.illustration : undefined)
                    }
                    link={header?.link ?? ('link' in data ? data.link : undefined)}
                    backLink={header?.backLink ?? backLink}
                    attributes={header?.attributes}
                  >
                    <Box flexDirection="row" gap="s16" alignItems="center">
                      {widget}
                    </Box>
                  </Title>
                )}
              </Box>
            )}
            <Box
              flexGrow
              position="relative"
              width="full"
              {...(props.height && { height: props.height })}
            >
              {children}
            </Box>
          </>
        ) : (
          <Box height="full" justifyContent="center" alignItems="center">
            <BlankSlate
              illustration="sign-post"
              title={i18n.t('errors.page_not_found.title')}
              description={i18n.t('errors.page_not_found.description')}
            >
              {pathname in favoriteIdByUrl && (
                <F0Button
                  variant="critical"
                  loading={isRemovingFavorite}
                  label={i18n.t('common.remove_favorite')}
                  onClick={() => {
                    removeFromFavorites({ variables: { id: favoriteIdByUrl[pathname] ?? 0 } })
                  }}
                />
              )}
            </BlankSlate>
          </Box>
        )}
      </ErrorBoundary>
    </Container>
  )
}

function Container({ children, variant, transactionalActions }: BodyProperties) {
  const Layout = variant === 'fullscreen' ? Box : StandardLayout

  return (
    // This needs to be a div to make the layout work as the parent forces flex-1 on its immediate children.
    // We might want to revisit this in the future.
    //
    // Overflow auto also needs to be forced here so it cascades to the children.
    <Box style={{ overflow: 'auto' }}>
      <Layout data-scroll-container style={{ height: '100%', overflow: 'auto', width: '100%' }}>
        {children}
      </Layout>
      {transactionalActions && (
        <Box border={{ top: { color: 'grey200', style: 'solid', width: 's1' } }} padding="s12">
          <TransactionalActions data={transactionalActions} />
        </Box>
      )}
    </Box>
  )
}

const getHeaderRenderStrategy = ({
  showHeader,
  hasResourceHeader,
  isResourcePage,
}: {
  showHeader: boolean
  hasResourceHeader: boolean
  isResourcePage: boolean
}): 'widget' | 'all' | 'none' => {
  if (!showHeader) return 'none'

  if (!isResourcePage) {
    return 'widget'
  }

  return hasResourceHeader ? 'none' : 'all'
}
